<template>
  <div class="dashboard">
    <a-card title="实时概况" :bordered="false">
      <template #extra>更新时间: {{ updateTime }}</template>
      <a-row :gutter="16">
        <a-col :span="6">
          <a-card class="stat-card">
            <div class="stat-title">应用总数</div>
            <div class="stat-number">{{ stats.products.total }}个</div>
            <div class="stat-detail">
              <span>订阅 {{ stats.products.subscribed }}</span>
              <span>自建 {{ stats.products.selfBuilt }}</span>
            </div>
          </a-card>
        </a-col>
        <a-col :span="6">
          <a-card class="stat-card">
            <div class="stat-title">设备</div>
            <div class="stat-number">{{ stats.devices.total }}台</div>
            <div class="stat-detail">
              <span>在线 {{ stats.devices.online }}</span>
              <span>离线 {{ stats.devices.offline }}</span>
            </div>
          </a-card>
        </a-col>
        <a-col :span="6">
          <a-card class="stat-card">
            <div class="stat-title">设备上报</div>
            <div class="stat-number">{{ stats.reports.total }}条</div>
            <div class="stat-detail">
              <span>昨日 {{ stats.reports.today }}</span>
              <span>本周 {{ stats.reports.week }}</span>
            </div>
          </a-card>
        </a-col>
        <a-col :span="6">
          <a-card class="stat-card">
            <div class="stat-title">当前在线用户数</div>
            <div class="stat-number">212 人</div>
            <div class="stat-detail">
              <span>昨日 558 人</span>
              <span>本周平均 510 人</span>
            </div>
          </a-card>
        </a-col>
      </a-row>
    </a-card>

    <a-card title="数据统计" :bordered="false" style="margin-top: 24px">
      <template #extra>
        <a-radio-group v-model:value="timeRange">
          <a-radio-button value="today">今日</a-radio-button>
          <a-radio-button value="7d">7天</a-radio-button>
          <a-radio-button value="30d">30天</a-radio-button>
          <a-radio-button value="12m">12个月</a-radio-button>
        </a-radio-group>
      </template>
      <div ref="chartRef" style="height: 300px"></div>
    </a-card>

    <a-row :gutter="16" style="margin-top: 24px">
      <a-col :span="12">
        <a-card title="订阅应用排行榜" :bordered="false">
          <div ref="productChartRef" style="height: 300px"></div>
        </a-card>
      </a-col>
      <a-col :span="12">
        <a-card title="设备状态预览" :bordered="false">
          <div ref="deviceChartRef" style="height: 300px"></div>
        </a-card>
      </a-col>
    </a-row>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, reactive, onUnmounted, computed, watch } from 'vue' // 引入 watch
import * as echarts from 'echarts'
import { deviceDataStore } from '../data/deviceData'
import { tenantList as tenantData } from '../data/tenantData'
import { appList as appData } from '../data/appData'

const updateTime = ref('');
const timeRange = ref('today');
const chartRef = ref();
const productChartRef = ref();
const deviceChartRef = ref();

// 添加 timer 变量定义
let timer: number | undefined;

// 更新时间的函数
const updateCurrentTime = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  //const second = String(now.getSeconds()).padStart(2, '0');
  updateTime.value = `${year}-${month}-${day} ${hours}:${minutes}`;
};

const stats = reactive({
  products: { 
    total: computed(() => {
      const selfBuiltCount = tenantData.reduce((acc, tenant) => acc + (tenant.apps ? tenant.apps.length : 0), 0);
      const subscribedCount = tenantData.reduce((acc, tenant) => acc + (tenant.subscriptions ? tenant.subscriptions.length : 0), 0);
      return selfBuiltCount + subscribedCount;
    }), 
    subscribed: computed(() => tenantData.reduce((acc, tenant) => acc + (tenant.subscriptions ? tenant.subscriptions.length : 0), 0)),
    selfBuilt: computed(() => tenantData.reduce((acc, tenant) => acc + (tenant.apps ? tenant.apps.length : 0), 0))
  },
  devices: { 
    total: deviceDataStore.length, 
    online: deviceDataStore.filter(item => item.status === '在线').length, 
    offline: deviceDataStore.filter(item => item.status === '离线').length
  },
  reports: { 
    total: computed(() => { 
      const now = new Date();
      let startTime = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // 默认今天0点

      switch (timeRange.value) {
        case '7d':
          startTime.setDate(now.getDate() - 6);
          break;
        case '30d':
          startTime.setDate(now.getDate() - 29);
          break;
        case '12m': 
          startTime.setDate(now.getDate() - 359); // 360天前
          break;
      }
      // 设置起始时间为当天的0点
      startTime.setHours(0, 0, 0, 0);

      const minutesPassed = Math.floor((now.getTime() - startTime.getTime()) / (1000 * 60));
      return 2700 * minutesPassed; 
    }), 
    today: computed(() => { 
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const minutesPassedToday = Math.floor((now.getTime() - todayStart.getTime()) / (1000 * 60));
      return 2700 * minutesPassedToday; 
    }), 
    week: computed(() => { // 本周（过去7天到当前时间）
        const now = new Date();
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - 6);
        weekStart.setHours(0,0,0,0);
        const minutesPassed = Math.floor((now.getTime() - weekStart.getTime()) / (1000 * 60));
        return 2700 * minutesPassed;
    })
  },
  controls: { total: 0, today: 0, week: 0 }
});

// 订阅应用排行数据
const subscriptionRanking = computed(() => {
  // 统计每个应用被订阅的次数
  const appSubscriptions = appData.map(app => {
    const subscriptionCount = tenantData.filter(tenant => 
      tenant.subscriptions && tenant.subscriptions.some(sub => sub.name === app.name)
    ).length
    
    return {
      name: app.name,
      value: subscriptionCount,
      id: app.id
    }
  })
  
  // 按订阅数量排序
  return appSubscriptions.sort((a, b) => b.value - a.value).slice(0, 10)
})

onMounted(() => {
  // 初始化时间
  updateCurrentTime();
  // 每分钟更新一次
  timer = window.setInterval(updateCurrentTime, 1000 * 60);
  // 初始化折线图
  const lineChart = echarts.init(chartRef.value);

  const getChartTimeRange = (range: string): { startTime: Date, endTime: Date, interval: 'hour' | 'day' | 'month' } => {
    const endTime = new Date(); // 截止时间为当前时间
    let startTime = new Date(endTime);
    let interval: 'hour' | 'day' | 'month' = 'hour';

    switch (range) {
      case 'today':
        startTime.setHours(0, 0, 0, 0);
        interval = 'hour';
        break;
      case '7d':
        startTime.setDate(endTime.getDate() - 6);
        startTime.setHours(0, 0, 0, 0);
        interval = 'day';
        break;
      case '30d':
        startTime.setDate(endTime.getDate() - 29);
        startTime.setHours(0, 0, 0, 0);
        interval = 'day';
        break;
      case '12m':
        startTime.setDate(endTime.getDate() - 359); // 360天前
        startTime.setHours(0, 0, 0, 0);
        interval = 'month'; // 对于12个月，X轴显示月份
        break;
    }
    return { startTime, endTime, interval };
  };

  const generateChartData = (startTime: Date, endTime: Date, interval: 'hour' | 'day' | 'month') => {
    const dataPoints: number[] = [];
    const onlinePoints: number[] = [];
    let current = new Date(startTime);
    const gpsReportsPerMinute = 2700;
    const baseOnline = deviceDataStore.filter(item => item.status === '在线').length;

    while (current <= endTime) {
      let reportsInInterval = 0;
      let nextPointTime = new Date(current);

      if (interval === 'hour') {
        nextPointTime.setHours(current.getHours() + 1);
        // 计算从current到nextPointTime（或endTime，如果更早）的分钟数
        const endOfInterval = nextPointTime < endTime ? nextPointTime : endTime;
        const minutesInInterval = Math.max(0, Math.floor((endOfInterval.getTime() - current.getTime()) / (1000 * 60)));
        reportsInInterval = gpsReportsPerMinute * minutesInInterval + Math.floor(Math.random() * (1000/24)); // 每小时随机数
        onlinePoints.push(baseOnline - Math.floor(Math.random() * 50));
      } else if (interval === 'day') {
        nextPointTime.setDate(current.getDate() + 1);
        const endOfInterval = nextPointTime < endTime ? nextPointTime : endTime;
        const minutesInInterval = Math.max(0, Math.floor((endOfInterval.getTime() - current.getTime()) / (1000 * 60)));
        reportsInInterval = gpsReportsPerMinute * minutesInInterval + Math.floor(Math.random() * 1000);
        onlinePoints.push(baseOnline - Math.floor(Math.random() * 50 * 24)); // 每日波动更大
      } else { // month
        nextPointTime.setMonth(current.getMonth() + 1);
        const endOfInterval = nextPointTime < endTime ? nextPointTime : endTime;
        const minutesInInterval = Math.max(0, Math.floor((endOfInterval.getTime() - current.getTime()) / (1000 * 60)));
        reportsInInterval = gpsReportsPerMinute * minutesInInterval + Math.floor(Math.random() * 1000 * 30); // 每月波动更大
        onlinePoints.push(baseOnline - Math.floor(Math.random() * 50 * 24 * 30));
      }
      dataPoints.push(reportsInInterval);
      current = nextPointTime;
      if (interval !== 'hour' && current > endTime && dataPoints.length > 0) {
        // 对于天和月，如果最后一个区间超出了endTime，确保最后一个点的数据是正确的
        const lastIntervalStart = new Date(current);
        if (interval === 'day') lastIntervalStart.setDate(current.getDate() -1);
        else lastIntervalStart.setMonth(current.getMonth()-1);
        const minutesInLastInterval = Math.max(0, Math.floor((endTime.getTime() - lastIntervalStart.getTime()) / (1000 * 60)));
        dataPoints[dataPoints.length -1] = gpsReportsPerMinute * minutesInLastInterval + Math.floor(Math.random() * (interval === 'day' ? 1000 : 1000*30));
      }
    }
    return { reportData: dataPoints, onlineData: onlinePoints };
  };

  const updateLineChart = (currentRangeValue: string) => {
    const { startTime, endTime, interval } = getChartTimeRange(currentRangeValue);
    const xAxisData = [];
    let tempTime = new Date(startTime);

    while (tempTime <= endTime) {
      if (interval === 'hour') {
        xAxisData.push(`${tempTime.getHours()}:00`);
        tempTime.setHours(tempTime.getHours() + 1);
      } else if (interval === 'day') {
        xAxisData.push(`${String(tempTime.getMonth() + 1).padStart(2, '0')}-${String(tempTime.getDate()).padStart(2, '0')}`);
        tempTime.setDate(tempTime.getDate() + 1);
      } else { // month
        xAxisData.push(`${tempTime.getFullYear()}-${String(tempTime.getMonth() + 1).padStart(2, '0')}`);
        tempTime.setMonth(tempTime.getMonth() + 1);
      }
    }
     // 如果最后一个点的时间戳超出了endTime，但数据点已生成，确保X轴标签数量匹配
    const chartData = generateChartData(startTime, endTime, interval);
    if (xAxisData.length > chartData.reportData.length && chartData.reportData.length > 0) {
        xAxisData.pop(); // 移除多余的X轴标签
    }
    if (xAxisData.length < chartData.reportData.length && xAxisData.length > 0) {
        // 理论上不应发生，但作为保险
        chartData.reportData = chartData.reportData.slice(0, xAxisData.length);
        chartData.onlineData = chartData.onlineData.slice(0, xAxisData.length);
    }


    lineChart.setOption({
      tooltip: { trigger: 'axis' },
      legend: {
        data: ['设备上报消息', '设备在线数量']
      },
      xAxis: {
        type: 'category',
        data: xAxisData 
      },
      yAxis: { type: 'value' },
      series: [
        {
          name: '设备上报消息',
          type: 'line',
          data: chartData.reportData
        },
        {
          name: '设备在线数量',
          type: 'line',
          data: chartData.onlineData
        }
      ]
    });
  };

  updateLineChart(timeRange.value); 

  watch(timeRange, (newRange) => {
    updateLineChart(newRange);
  });

  // 初始化产品状态饼图
  const productChart = echarts.init(productChartRef.value);
  productChart.setOption({
    title: {
      text: '订阅应用排行榜',
      left: 'center'
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'value',
      boundaryGap: [0, 0.01]
    },
    yAxis: {
      type: 'category',
      data: subscriptionRanking.value.map(item => item.name),
      axisLabel: {
        width: 100,
        overflow: 'truncate'
      }
    },
    series: [
      {
        name: '订阅数量',
        type: 'bar',
        data: subscriptionRanking.value.map(item => item.value),
        itemStyle: {
          color: function(params: echarts.DefaultLabelFormatterCallbackParams) {
            // 颜色渐变
            const colorList = ['#83bff6', '#188df0', '#005eaa', '#0e2a43'];
            return colorList[params.dataIndex % colorList.length];
          }
        },
        label: {
          show: true,
          position: 'right'
        }
      }
    ]
  });

  // 初始化设备状态饼图
  const deviceChart = echarts.init(deviceChartRef.value)
  deviceChart.setOption({
    tooltip: { trigger: 'item' },
    legend: { orient: 'vertical', left: 'left' },
    series: [{
      type: 'pie',
      radius: '50%',
      data: [
        {
          value: 0, 
          name: '未激活设备'
        },
        {
          value: 2700, 
          name: '在线设备' 
        },
        {
          value: 0, 
          name: '离线设备'
        }
      ]
    }]
  });
});

// 清理定时器
onUnmounted(() => {
  if (timer) {
    clearInterval(timer);
  }
});
</script>

<style lang="less" scoped>
.dashboard {
  padding: 24px;
  background: #f0f2f5;

  .stat-card {
    background: #fff;
    border-radius: 2px;

    .stat-title {
      color: rgba(0, 0, 0, 0.45);
      font-size: 14px;
    }

    .stat-number {
      margin: 8px 0;
      font-size: 24px;
      font-weight: 500;
    }

    .stat-detail {
      color: rgba(0, 0, 0, 0.65);
      font-size: 14px;
      
      span {
        margin-right: 16px;
      }
    }
  }
}
</style>